import React, { useEffect, useState } from "react";
import {
    LineChart,
    Line,
    Area,
    AreaChart,
    ResponsiveContainer,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    BarChart,
    Bar,
} from "recharts";
import {
    MessagesSquare,
    UserPlus,
    Diamond,
    LifeBuoy,
    FileCheck2,
    TrendingUp,
} from "lucide-react";
import { adminApi } from "@/lib/api";

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
    <div
        data-testid={testid}
        className="bg-white border border-stone-200 rounded-lg p-6 shadow-card hover:shadow-lift transition-shadow"
    >
        <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500">{label}</div>
            <Icon size={16} className="text-orchid-700/70" />
        </div>
        <div className="font-heading text-5xl text-orchid-700 leading-none">{value}</div>
        {sub && <div className="mt-2 text-xs text-stone-500">{sub}</div>}
    </div>
);

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        adminApi
            .get("/api/admin/stats")
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.detail || "Failed to load stats"));
    }, []);

    if (error) return <div className="p-8 text-red-700">{error}</div>;
    if (!data)
        return (
            <div className="p-12 flex items-center gap-3 text-stone-500">
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-dot" />
            </div>
        );

    return (
        <div className="p-10 max-w-7xl">
            <div className="mb-10">
                <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">
                    Overview
                </div>
                <h1 className="font-heading text-5xl text-orchid-700">Concierge Dashboard</h1>
                <p className="text-stone-600 mt-2 max-w-2xl">
                    A real-time look at every visitor interaction and lead captured by the Orchid
                    Concierge across the orchidhotel.com network.
                </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                <StatCard
                    icon={MessagesSquare}
                    label="Conversations"
                    value={data.total_conversations}
                    sub={`+${data.conversations_7d} this week`}
                    testid="stat-conversations"
                />
                <StatCard
                    icon={UserPlus}
                    label="Leads Captured"
                    value={data.total_leads}
                    sub={`+${data.leads_7d} this week`}
                    testid="stat-leads"
                />
                <StatCard
                    icon={LifeBuoy}
                    label="Open Tickets"
                    value={data.open_tickets}
                    sub={`${data.total_tickets} total`}
                    testid="stat-tickets"
                />
                <StatCard
                    icon={FileCheck2}
                    label="Documents Indexed"
                    value={data.documents_indexed}
                    sub="Knowledge base ready"
                    testid="stat-documents"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
                {/* Conversations over time */}
                <div className="lg:col-span-2 bg-white border border-stone-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <div className="text-xs uppercase tracking-[0.2em] text-stone-500">
                                Last 7 days
                            </div>
                            <h3 className="font-heading text-2xl text-orchid-700">
                                Conversations
                            </h3>
                        </div>
                        <TrendingUp size={16} className="text-orchid-700/60" />
                    </div>
                    <div className="h-64 min-h-64 w-full" data-testid="chart-conversations">
                        <ResponsiveContainer width="100%" height={256} minWidth={0}>
                            <AreaChart data={data.daily_series}>
                                <defs>
                                    <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#7E2A6C" stopOpacity={0.3} />
                                        <stop offset="100%" stopColor="#7E2A6C" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fill: "#78716c", fontSize: 11 }}
                                    tickFormatter={(d) => (d ? d.slice(5) : "")}
                                />
                                <YAxis tick={{ fill: "#78716c", fontSize: 11 }} allowDecimals={false} />
                                <Tooltip
                                    contentStyle={{
                                        background: "white",
                                        border: "1px solid #e7e5e4",
                                        borderRadius: 8,
                                        fontSize: 12,
                                    }}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="conversations"
                                    stroke="#7E2A6C"
                                    strokeWidth={2}
                                    fill="url(#g1)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Lead types */}
                <div className="bg-white border border-stone-200 rounded-lg p-6">
                    <div className="mb-6">
                        <div className="text-xs uppercase tracking-[0.2em] text-stone-500">
                            Leads by Type
                        </div>
                        <h3 className="font-heading text-2xl text-orchid-700">Pipeline</h3>
                    </div>
                    <div className="space-y-4">
                        {[
                            { key: "booking", label: "Booking", color: "#7E2A6C" },
                            { key: "wedding", label: "Wedding & Events", color: "#D4AF37" },
                            { key: "membership", label: "Membership", color: "#C026D3" },
                        ].map((row) => {
                            const v = data.by_type[row.key] || 0;
                            const total =
                                (data.by_type.booking || 0) +
                                (data.by_type.wedding || 0) +
                                (data.by_type.membership || 0);
                            const pct = total ? (v / total) * 100 : 0;
                            return (
                                <div key={row.key}>
                                    <div className="flex justify-between text-sm mb-1.5">
                                        <span className="text-stone-700">{row.label}</span>
                                        <span className="font-medium text-stone-900">{v}</span>
                                    </div>
                                    <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full rounded-full transition-all"
                                            style={{
                                                width: `${pct}%`,
                                                background: row.color,
                                            }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Top intents */}
            {data.top_intents?.length > 0 && (
                <div className="bg-white border border-stone-200 rounded-lg p-6">
                    <div className="mb-6">
                        <div className="text-xs uppercase tracking-[0.2em] text-stone-500">
                            Top Intents
                        </div>
                        <h3 className="font-heading text-2xl text-orchid-700">
                            What guests are asking
                        </h3>
                    </div>
                    <div className="h-48 min-h-48 w-full" data-testid="chart-intents">
                        <ResponsiveContainer width="100%" height={192} minWidth={0}>
                            <BarChart data={data.top_intents}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                                <XAxis dataKey="tag" tick={{ fill: "#78716c", fontSize: 11 }} />
                                <YAxis tick={{ fill: "#78716c", fontSize: 11 }} allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="count" fill="#7E2A6C" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
        </div>
    );
}
